import type { IntlShape } from 'react-intl'
import config from 'constants/config'
import { formatCurrency } from 'lib/format/formatCurrencyIntl'

/** Anchor ID for the "Discover a world of rewards" tiers section on the status match page. */
export const DISCOVER_TIERS_ID = 'discover-tiers'

function formatBenefitCurrencyAmount(amount: number, currencyCode?: string): string {
  const resolvedCurrency = currencyCode
    ?? config.regions.find(region => region.code === config.defaultRegion)?.currencyCode
    ?? 'AUD'
  const regionCode = config.regions.find(region => region.currencyCode === resolvedCurrency)?.code
    ?? config.defaultRegion

  return formatCurrency(amount, resolvedCurrency, regionCode, { format: 'roundedDollar' })
}

export function getLoyaltyEsimGbAmount(benefit: App.LoyaltyBenefit | undefined): number | undefined {
  return benefit?.value?.unit === 'GB' ? benefit.value.amount : undefined
}

export function formatLoyaltyEsimInternationalDataHeading(
  intl: IntlShape,
  amount: number | string,
): string {
  return intl.formatMessage(
    {
      defaultMessage: '{amount} GB Free International Data',
      id: 'oTKHBJeWGL-bar8FwzXj',
      description: 'Heading for eSIM benefit tile on account landing page',
    },
    { amount },
  )
}

/**
 * Returns a complete, i18n-friendly benefit label for a loyalty tier benefit.
 * The count, unit (GB / %), and noun are composed in a single ICU message so
 * translators can reorder them per locale. Returns undefined for unknown codes.
 *
 * Shared between the tier upgrade campaign cards, status match tier cards,
 * and the status match success modal benefits list.
 */
export function formatBenefitLabel(
  intl: IntlShape,
  benefit: App.LoyaltyRegionTierBenefit,
): string | undefined {
  const count = benefit.limit ?? 0
  const value = benefit.value?.unit === 'CURRENCY'
    ? formatBenefitCurrencyAmount(benefit.value.amount, benefit.value.currency)
    : undefined
  switch (benefit.code) {
    case 'hotel_upgrade':
      if (benefit.unlimited) {
        return intl.formatMessage({
          id: 'fOS8MQ0CdyADxFXhkZl3',
          defaultMessage: 'Unlimited free room or package upgrades',
          description: 'Tier card benefit description for unlimited room upgrades',
        })
      }
      return count === 1 ?
          intl.formatMessage({
            id: 'RGEAXLURt6yObQLBDg0D',
            defaultMessage: '{count} free room or package upgrade',
            description: 'Tier card benefit description for one room upgrade',
          }, { count }) :
          intl.formatMessage({
            id: 'gnOcoOd1aayfSDcT1e4f',
            defaultMessage: '{count} free room or package upgrades',
            description: 'Tier card benefit description for multiple room upgrades',
          }, { count })
    case 'lounge_pass':
      if (benefit.unlimited) {
        return intl.formatMessage({
          id: 'ywV29RXpLnMIHig2N5rY',
          defaultMessage: 'Unlimited free airport lounge passes',
          description: 'Tier card benefit description for unlimited lounge passes',
        })
      }
      return count === 1 ?
          intl.formatMessage({
            id: 'EBScqD97j6Pf81c6xtcB',
            defaultMessage: '{count} free airport lounge pass',
            description: 'Tier card benefit description for one airport lounge pass',
          }, { count }) :
          intl.formatMessage({
            id: '7yGYoumE-AOwyS4ztBUm',
            defaultMessage: '{count} free airport lounge passes',
            description: 'Tier card benefit description for multiple lounge passes',
          }, { count })
    case 'airport_transfer':
      if (benefit.unlimited) {
        return intl.formatMessage({
          id: '93mTEiCVgTw8LxzdLyb3',
          defaultMessage: 'Unlimited free airport chauffeur transfers',
          description: 'Tier card benefit description for unlimited airport transfers',
        })
      }
      return count === 1 ?
          intl.formatMessage({
            id: '02JBF_enBkcUejqW3mHF',
            defaultMessage: '{count} free airport chauffeur transfer',
            description: 'Tier card benefit description for one airport transfer',
          }, { count }) :
          intl.formatMessage({
            id: '45qmB7dhKwta4MPdxBLt',
            defaultMessage: '{count} free airport chauffeur transfers',
            description: 'Tier card benefit description for multiple airport transfers',
          }, { count })
    case 'tour_exp_credit':
      if (value) {
        return count === 1 ?
            intl.formatMessage({
              id: 'nLA-AKoIMM8sf-OxTnUk',
              defaultMessage: '{value} tour credit',
              description: 'Tier card benefit description for one tour credit with a monetary value',
            }, { value }) :
            intl.formatMessage({
              id: 'fe4Mxrt4ur1mO-z5JcEg',
              defaultMessage: '{value} tour credits',
              description: 'Tier card benefit description for multiple tour credits with a monetary value',
            }, { value })
      }
      if (benefit.unlimited) {
        return intl.formatMessage({
          id: '4Y-K4nHWI7jvOaSxCy3f',
          defaultMessage: 'Unlimited tour credits',
          description: 'Tier card benefit description for unlimited tour credits',
        })
      }
      return count === 1 ?
          intl.formatMessage({
            id: 'e1mY7CSj-VP5Fq2xAXi0',
            defaultMessage: '{count} tour credit',
            description: 'Tier card benefit description for one limited tour credit',
          }, { count }) :
          intl.formatMessage({
            id: 'U9QKWNoDVMWpGMljclai',
            defaultMessage: '{count} tour credits',
            description: 'Tier card benefit description for multiple tour credits',
          }, { count })
    case 'phone_support':
      return intl.formatMessage({
        id: 'qWYrWOkV8rz1f6WYS5bV',
        defaultMessage: '24/7 priority phone support',
        description: 'Tier card benefit description for 24/7 priority phone support',
      })
    case 'discounted_insurance':
      return intl.formatMessage({
        id: 'DTJtZ-VSAHSsZrB360iy',
        defaultMessage: '{discount}% off travel insurance',
        description: 'Tier card benefit description for discounted travel insurance',
      }, { discount: benefit.value?.amount ?? 0 })
    case 'esim_package':
      return intl.formatMessage({
        id: 'hmq_cfLuUUgFdWYt5T1d',
        defaultMessage: '{amount}GB travel eSIM per international trip',
        description: 'Tier card benefit description for complimentary travel eSIM data package',
      }, { amount: benefit.value?.amount ?? 0 })
    default:
      return undefined
  }
}

/**
 * Preferred display order for loyalty benefits on the Status Match page.
 * Codes not listed here keep their original order at the end.
 */
const BENEFIT_DISPLAY_ORDER: Array<App.LoyaltyBenefitType> = [
  'hotel_upgrade',
  'airport_transfer',
  'lounge_pass',
  'tour_exp_credit',
  'esim_package',
  'phone_support',
  'discounted_insurance',
]

/** Returns benefits sorted by the preferred status-match display order. */
export function sortBenefitsForDisplay<T extends { code: App.LoyaltyBenefitType }>(benefits: ReadonlyArray<T>): Array<T> {
  const indexOf = (code: App.LoyaltyBenefitType) => {
    const i = BENEFIT_DISPLAY_ORDER.indexOf(code)
    return i === -1 ? Number.MAX_SAFE_INTEGER : i
  }
  return [...benefits].sort((a, b) => indexOf(a.code) - indexOf(b.code))
}
